import React from 'react';
import {
  BarChart2,
  ChevronDown,
  Play,
  RefreshCcw,
  Settings,
  Square,
  Trash2,
} from 'lucide-react';

const TopControlsSection = ({ controller }) => {
  const {
    datasetBadgeText,
    datasetDropdownRef,
    datasetSearch,
    setDatasetSearch,
    datasets,
    setSelectedDataset,
    setIsDatasetDropdownOpen,
    isDatasetDropdownOpen,
    isLoadingDatasets,
    filteredDatasets,
    datasetLoadError,
    selectedDataset,
    refreshDatasets,
    isRefreshingDatasets,
    selectDataset,
    modelName,
    handleModelChange,
    isChangingModel,
    resizeMode,
    handleResizeModeChange,
    isChangingResizeMode,
    compressionMode,
    handleCompressionModeChange,
    isChangingCompressionMode,
    useEbayRag,
    handleUseEbayRagChange,
    isChangingEbayRag,
    useNotesRag,
    handleUseNotesRagChange,
    isChangingNotesRag,
    assumeHiddenItems,
    handleAssumeHiddenItemsChange,
    isChangingAssumeHidden,
    runTestRig,
    isStartingRun,
    isRunning,
    fetchData,
    fetchDatasets,
    flushResults,
    isFlushing,
    isRunInProgress,
    testResults,
    loading,
    visiblePromptTemplate,
    activeRuns,
    terminateAllRuns,
    isTerminatingAllRuns,
    terminatingRunIds,
    terminateRun,
    getRunProgressMetrics,
    formatStartedAt,
    formatResizeDisplay,
    formatEta,
    primaryRunProgress,
  } = controller;

  return (
    <div className="flex flex-col gap-3 mb-6 sm:mb-8">
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-2">
        <h1 className="text-2xl sm:text-3xl font-bold text-ink">Test Rig</h1>
        <div className="flex items-center px-3 sm:px-4 py-2 bg-accent/10 text-accent rounded border border-accent/20">
          <BarChart2 size={18} className="mr-2 flex-shrink-0" />
          <span className="text-xs sm:text-sm font-medium">Dataset: {datasetBadgeText}</span>
        </div>
      </div>

      <div className="flex flex-col md:flex-row md:items-stretch md:flex-nowrap gap-2">
        <div ref={datasetDropdownRef} className="relative md:flex-[2.4]">
          <div className="flex items-center bg-surface-2 p-2 rounded-lg border border-hairline h-full">
            <span className="text-sm font-medium text-ink mr-2">Dataset:</span>
            <input
              value={datasetSearch}
              onChange={(e) => {
                const nextValue = e.target.value;
                const normalized = nextValue.trim().toLowerCase();
                const exactMatch =
                  datasets.find((dataset) => {
                    const datasetName = (dataset?.name || '').trim().toLowerCase();
                    const datasetPrefix = (dataset?.prefix || '').trim().toLowerCase();
                    return normalized && (datasetName === normalized || datasetPrefix === normalized);
                  }) || null;
                setDatasetSearch(nextValue);
                setSelectedDataset(exactMatch);
                setIsDatasetDropdownOpen(true);
              }}
              onFocus={() => setIsDatasetDropdownOpen(true)}
              disabled={isLoadingDatasets}
              placeholder={isLoadingDatasets ? 'Loading datasets...' : 'Search datasets...'}
              className={`w-full bg-surface border border-hairline rounded px-2 py-1 text-ink outline-none focus:border-accent focus:ring-2 focus:ring-accent/30 placeholder:text-muted text-xs font-medium ${isLoadingDatasets ? 'opacity-60 cursor-not-allowed' : ''}`}
            />
            <button
              type="button"
              onMouseDown={(event) => event.preventDefault()}
              onClick={() => setIsDatasetDropdownOpen((previous) => !previous)}
              className="ml-2 inline-flex items-center justify-center rounded border border-hairline bg-surface px-1.5 py-1 text-muted hover:bg-surface-2"
              title={isDatasetDropdownOpen ? 'Close dataset list' : 'Open dataset list'}
              aria-label={isDatasetDropdownOpen ? 'Close dataset list' : 'Open dataset list'}
            >
              <ChevronDown size={14} className={`transition-transform ${isDatasetDropdownOpen ? 'rotate-180' : ''}`} />
            </button>
            <button
              type="button"
              onMouseDown={(event) => event.preventDefault()}
              onClick={() => refreshDatasets()}
              disabled={isRefreshingDatasets}
              className={`ml-1 inline-flex items-center justify-center rounded border px-1.5 py-1 transition-colors ${isRefreshingDatasets ? 'border-hairline bg-surface-2 text-muted cursor-not-allowed' : 'border-accent/20 bg-accent/10 text-accent hover:bg-accent/20'}`}
              title="Repull dataset list from blob storage"
              aria-label="Repull dataset list from blob storage"
            >
              <RefreshCcw size={14} className={isRefreshingDatasets ? 'animate-spin' : ''} />
            </button>
          </div>
          {isDatasetDropdownOpen && (
            <div className="absolute z-20 mt-1 w-full max-h-60 overflow-y-auto rounded-lg border border-hairline bg-surface shadow-xl">
              {isLoadingDatasets ? (
                <div className="px-3 py-2 text-xs text-muted">Loading datasets...</div>
              ) : filteredDatasets.length === 0 ? (
                <div className="px-3 py-2 text-xs text-muted">
                  {datasetLoadError
                    ? `Failed to load datasets: ${datasetLoadError}`
                    : datasets.length > 0
                      ? 'No matching datasets.'
                      : 'No datasets available.'}
                </div>
              ) : (
                filteredDatasets.map((dataset) => (
                  <button
                    key={dataset.id || dataset.prefix || '__root__'}
                    type="button"
                    onMouseDown={(e) => {
                      e.preventDefault();
                      selectDataset(dataset);
                    }}
                    className={`w-full text-left px-3 py-2 text-xs border-b border-hairline last:border-b-0 hover:bg-surface-2 ${(selectedDataset?.prefix || '') === (dataset.prefix || '') ? 'bg-accent/10 text-accent' : 'text-ink'}`}
                  >
                    <div className="font-semibold">{dataset.name}</div>
                    <div className="text-[11px] text-muted">
                      {(dataset.prefix || '__root__')} • {dataset.data_points || 0} data points • {dataset.image_count || 0} images
                    </div>
                  </button>
                ))
              )}
            </div>
          )}
        </div>

        <div className="flex items-center bg-surface-2 p-2 rounded-lg border border-hairline md:flex-[1.6]">
          <Settings size={18} className="text-muted mr-2" />
          <span className="text-sm font-medium text-ink mr-2">Model:</span>
          <select
            value={modelName}
            onChange={(e) => handleModelChange(e.target.value)}
            disabled={isChangingModel}
            className={`bg-surface border border-hairline rounded px-2 py-1 text-ink outline-none focus:border-accent focus:ring-2 focus:ring-accent/30 text-xs font-medium w-full ${isChangingModel ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer'}`}
          >
            <optgroup label="GPT-5 Series (Latest)">
              <option value="gpt-5.4">gpt-5.4</option>
              <option value="gpt-5.4-mini">gpt-5.4-mini</option>
              <option value="gpt-5.2">gpt-5.2</option>
              <option value="gpt-5.1">gpt-5.1</option>
              <option value="gpt-5">gpt-5</option>
            </optgroup>
            <optgroup label="Gemini">
              <option value="gemini-flash-latest">gemini-flash-latest</option>
              <option value="gemini-2.5-pro">gemini-2.5-pro</option>
              <option value="gemini-2.5-flash">gemini-2.5-flash</option>
              <option value="gemini-2.0-flash">gemini-2.0-flash</option>
            </optgroup>
            <optgroup label="Claude">
              <option value="claude-sonnet-4-5">claude-sonnet-4-5</option>
              <option value="claude-haiku-4-5">claude-haiku-4-5</option>
              <option value="claude-opus-4-6">claude-opus-4-6</option>
              <option value="claude-opus-4-7">claude-opus-4-7</option>
              <option value="claude-fable-5">claude-fable-5</option>
            </optgroup>
            <optgroup label="GPT-4 Series">
              <option value="gpt-4o">gpt-4o</option>
              <option value="gpt-4o-mini">gpt-4o-mini</option>
              <option value="gpt-4.1">gpt-4.1</option>
              <option value="gpt-4.1-mini">gpt-4.1-mini</option>
            </optgroup>
            <optgroup label="Reasoning Models">
              <option value="o1">o1</option>
              <option value="o3-mini">o3-mini</option>
              <option value="o3">o3</option>
              <option value="o4-mini">o4-mini</option>
            </optgroup>
          </select>
          {isChangingModel && <span className="text-xs text-muted ml-2">Updating...</span>}
        </div>

        <div className="flex items-center bg-surface-2 p-2 rounded-lg border border-hairline md:flex-[1.2]">
          <span className="text-sm font-medium text-ink mr-2">Resize:</span>
          <select value={resizeMode} onChange={(e) => handleResizeModeChange(e.target.value)} disabled={isChangingResizeMode} className={`bg-surface border border-hairline rounded px-2 py-1 text-ink outline-none focus:border-accent focus:ring-2 focus:ring-accent/30 text-xs font-medium w-full ${isChangingResizeMode ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer'}`}>
            <option value="original">Original</option>
            <option value="1600">Max 1600px</option>
            <option value="1280">Max 1280px</option>
            <option value="1024">Max 1024px</option>
            <option value="768">Max 768px</option>
            <option value="512">Max 512px</option>
            <option value="128">Max 128px</option>
          </select>
          {isChangingResizeMode && <span className="text-xs text-muted ml-2">Updating...</span>}
        </div>

        <div className="flex items-center bg-surface-2 p-2 rounded-lg border border-hairline md:flex-[1.2]">
          <span className="text-sm font-medium text-ink mr-2">Compression:</span>
          <select value={compressionMode} onChange={(e) => handleCompressionModeChange(e.target.value)} disabled={isChangingCompressionMode} className={`bg-surface border border-hairline rounded px-2 py-1 text-ink outline-none focus:border-accent focus:ring-2 focus:ring-accent/30 text-xs font-medium w-full ${isChangingCompressionMode ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer'}`}>
            <option value="none">None</option>
            <option value="low">Low</option>
            <option value="medium">Medium</option>
            <option value="high">High</option>
          </select>
          {isChangingCompressionMode && <span className="text-xs text-muted ml-2">Updating...</span>}
        </div>

        <div className="flex items-center bg-surface-2 p-2 rounded-lg border border-hairline md:flex-[1.2]">
          <span className="text-sm font-medium text-ink mr-2 whitespace-nowrap">eBay RAG:</span>
          <button type="button" onClick={() => handleUseEbayRagChange(!useEbayRag)} disabled={isChangingEbayRag} className={`w-full rounded px-2 py-1 text-xs font-medium border transition-colors ${useEbayRag ? 'border-accent bg-accent/10 text-accent' : 'border-hairline bg-surface text-muted'} ${isChangingEbayRag ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer hover:bg-surface-2'}`}>
            {isChangingEbayRag ? 'Updating...' : useEbayRag ? 'Enabled' : 'Disabled'}
          </button>
        </div>

        <div className="flex items-center bg-surface-2 p-2 rounded-lg border border-hairline md:flex-[1.2]">
          <span className="text-sm font-medium text-ink mr-2 whitespace-nowrap">Notes RAG:</span>
          <button type="button" onClick={() => handleUseNotesRagChange(!useNotesRag)} disabled={isChangingNotesRag} className={`w-full rounded px-2 py-1 text-xs font-medium border transition-colors ${useNotesRag ? 'border-positive bg-positive/10 text-positive' : 'border-hairline bg-surface text-muted'} ${isChangingNotesRag ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer hover:bg-surface-2'}`}>
            {isChangingNotesRag ? 'Updating...' : useNotesRag ? 'Enabled' : 'Disabled'}
          </button>
        </div>

        <div className="flex items-center bg-surface-2 p-2 rounded-lg border border-hairline md:flex-[1.2]">
          <span className="text-sm font-medium text-ink mr-2 whitespace-nowrap">Hidden Items:</span>
          <button type="button" onClick={() => handleAssumeHiddenItemsChange(!assumeHiddenItems)} disabled={isChangingAssumeHidden} className={`w-full rounded px-2 py-1 text-xs font-medium border transition-colors ${assumeHiddenItems ? 'border-ai bg-ai/10 text-ai' : 'border-hairline bg-surface text-muted'} ${isChangingAssumeHidden ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer hover:bg-surface-2'}`}>
            {isChangingAssumeHidden ? 'Updating...' : assumeHiddenItems ? 'Enabled' : 'Disabled'}
          </button>
        </div>
      </div>

      <div className="flex items-center gap-2 sm:gap-3 md:justify-end">
        <button onClick={runTestRig} disabled={isStartingRun || isRunning === null || !selectedDataset} className={`flex items-center flex-1 sm:flex-none justify-center px-4 py-2 rounded text-white font-medium transition-colors ${isStartingRun || isRunning === null || !selectedDataset ? 'bg-disabled cursor-not-allowed' : 'bg-brand hover:bg-brand-hover'}`}>
          <Play size={18} className="mr-1.5" />
          <span className="text-sm">{isStartingRun ? 'Starting...' : isRunning === null ? 'Checking...' : 'Run Test'}</span>
        </button>
        <button onClick={() => { fetchData(); fetchDatasets(); }} className="flex items-center px-3 py-2 bg-surface-2 text-ink rounded hover:bg-surface-2/70 transition-colors" title="Refresh data">
          <RefreshCcw size={18} className={loading ? 'animate-spin' : ''} />
          <span className="text-sm ml-1.5 hidden sm:inline">Refresh</span>
        </button>
        <button onClick={flushResults} disabled={isFlushing || isRunInProgress || isRunning === null || testResults.length === 0} className={`flex items-center px-3 py-2 rounded text-white transition-colors ${isFlushing || isRunInProgress || isRunning === null || testResults.length === 0 ? 'bg-disabled cursor-not-allowed' : 'bg-brand hover:bg-brand-hover'}`} title="Clear all test results">
          <Trash2 size={18} className={isFlushing ? 'animate-spin' : ''} />
          <span className="text-sm ml-1.5 hidden sm:inline">{isFlushing ? 'Flushing...' : 'Flush'}</span>
        </button>
      </div>

      <div className="bg-surface-2 rounded-lg border border-hairline p-3">
        <div className="text-[11px] font-semibold uppercase tracking-wide text-muted mb-2">Prompt Template</div>
        <pre className="m-0 text-[11px] leading-relaxed whitespace-pre-wrap break-words text-ink font-mono max-h-48 overflow-y-auto">
          {loading ? 'Loading prompt template...' : visiblePromptTemplate || 'No prompt template available from backend code.'}
        </pre>
      </div>

      {loading && activeRuns.length === 0 && (
        <div className="bg-surface-2 rounded-lg border border-hairline p-3 text-xs text-muted">
          Checking for running experiments...
        </div>
      )}

      {activeRuns.length > 0 && (
        <div className="bg-accent/10 rounded-lg border border-accent/20 p-3">
          <div className="flex items-center justify-between gap-2 mb-2">
            <div className="text-xs font-semibold uppercase tracking-wide text-accent">
              Running Experiments ({activeRuns.length})
            </div>
            <button type="button" onClick={terminateAllRuns} disabled={isTerminatingAllRuns} className={`inline-flex items-center px-2.5 py-1.5 rounded text-xs font-semibold text-white transition-colors ${isTerminatingAllRuns ? 'bg-disabled cursor-not-allowed' : 'bg-brand hover:bg-brand-hover'}`}>
              <Square size={13} className="mr-1.5" />
              {isTerminatingAllRuns ? 'Terminating...' : 'Terminate All'}
            </button>
          </div>
          <div className="space-y-2">
            {activeRuns.map((run, idx) => {
              const runKey = run.run_id || String(run.pid || idx);
              const isTerminatingRun = !!terminatingRunIds[run.run_id];
              const progress = getRunProgressMetrics(run);
              const processedLabel = progress.total !== null ? `${progress.attempted}/${progress.total}${progress.datasetTruncated ? ' (truncated)' : ''}` : `${progress.attempted}/?`;
              return (
                <div key={runKey} className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 bg-surface border border-hairline rounded p-2.5">
                  <div className="text-xs text-ink flex-1 min-w-0">
                    <div className="font-semibold">{(run.dataset_name || run.dataset_prefix || 'Container root')} • {run.model || 'N/A'}</div>
                    <div className="text-[11px] text-muted">
                      {formatStartedAt(run.started_at)} • resize {formatResizeDisplay(run.resize_mode)} • compression {run.compression_mode || 'none'} • eBay RAG {run.use_ebay_rag ? 'on' : 'off'} • Notes RAG {run.use_notes_rag ? 'on' : 'off'} • Hidden Items {run.assume_hidden_items ? 'on' : 'off'}
                    </div>
                    <div className="mt-1 text-[11px] text-accent">
                      Progress {processedLabel} ({progress.progressPercent.toFixed(1)}%) • success {progress.successful} • skipped {progress.skipped} • ETA {progress.attempted > 0 ? formatEta(progress.etaSeconds) : 'N/A'}
                      {progress.currentLot ? ` • lot #${progress.currentLot}` : ''}
                      {progress.currentAttempt ? ` • attempt ${progress.currentAttempt}` : ''}
                    </div>
                    <div className="mt-1.5 h-1.5 bg-accent/20 rounded overflow-hidden">
                      <div className="h-full bg-brand transition-all duration-500" style={{ width: `${Math.max(0, Math.min(100, progress.progressPercent))}%` }} />
                    </div>
                    {progress.lastError && (
                      <div className="mt-1 text-[11px] text-warn truncate" title={progress.lastError}>
                        Last error: {progress.lastError}
                      </div>
                    )}
                  </div>
                  <button type="button" onClick={() => terminateRun(run.run_id)} disabled={!run.run_id || isTerminatingRun} className={`inline-flex items-center justify-center px-2.5 py-1.5 rounded text-xs font-semibold text-white transition-colors ${!run.run_id || isTerminatingRun ? 'bg-disabled cursor-not-allowed' : 'bg-brand hover:bg-brand-hover'}`}>
                    <Square size={13} className="mr-1.5" />
                    {isTerminatingRun ? 'Terminating...' : 'Terminate'}
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      )}

      <div className="mb-10 p-10 bg-surface-2 rounded-lg border border-hairline text-center">
        <div className="flex flex-col items-center justify-center max-w-lg mx-auto">
          <BarChart2 size={64} className="text-muted mb-4" />
          <h2 className="text-xl font-bold text-ink mb-2">Detailed Analytics Engine</h2>
          <div className="w-full bg-hairline h-2 rounded-full overflow-hidden">
            <div className="bg-positive h-full transition-all duration-1000" style={{ width: loading ? '35%' : isRunInProgress ? `${Math.max(2, Math.min(100, primaryRunProgress?.progressPercent ?? 0))}%` : '100%' }} />
          </div>
          <p className="text-xs text-muted mt-2">
            {loading
              ? 'Loading test rig status...'
              : isRunInProgress
                ? `Processing ${primaryRunProgress?.attempted ?? 0}/${primaryRunProgress?.total ?? '?'}${primaryRunProgress?.datasetTruncated ? ' (truncated)' : ''} lots (${(primaryRunProgress?.progressPercent ?? 0).toFixed(1)}%), ETA ${(primaryRunProgress?.attempted ?? 0) > 0 ? formatEta(primaryRunProgress?.etaSeconds) : 'N/A'}`
                : 'System ready for full dataset pass'}
          </p>
        </div>
      </div>
    </div>
  );
};

export default TopControlsSection;
